import { Input } from "@xola/ui-kit";
import { DemographicIcon, isAutoGeneratedName } from "common";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Guest, Product, UnitType } from "types";
import { useDebouncedCallback } from "use-debounce";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { NoTranslate } from "../../Common/NoTranslate";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

export type OnChangeFunction = (guest: Guest, skipPurchaseUpdate?: boolean) => void;

interface QuestionnaireNameFieldProps {
    guest: Guest;
    product: Product;
    disableRequiredFields: boolean;
    onChange: OnChangeFunction;
}

export const QuestionnaireNameField = ({
    guest,
    product,
    disableRequiredFields,
    onChange,
}: QuestionnaireNameFieldProps) => {
    // Guests are keyed by unit; a secondary demographic's guest carries the global template id, which no
    // product catalog item matches. The unit id is the stable link, so try it before the template id.
    const template = product.catalog?.items.find(
        (item) =>
            (guest.unit?.id && item.unit?.id === guest.unit.id) ||
            (item.template?.id && item.template.id === guest.template?.id),
    );
    const isMembership = template?.unitType === UnitType.MEMBERSHIP;

    const guestIndex =
        useCreatePurchaseStore((state) => state.getActiveItem().guests?.findIndex((g) => g.id === guest.id) ?? 0) + 1;

    const guestsOfCurrentType =
        useCreatePurchaseStore((state) =>
            state.getActiveItem().guests?.filter((g) => g.template?.id === guest.template?.id),
        ) ?? [];

    const currentGuestIndex = guestsOfCurrentType.findIndex((g) => g.id === guest.id);

    const placeholderName = isMembership ? `Member #${guestIndex}` : `${template?.name} #${currentGuestIndex + 1}`;

    const [initialName] = useState(guest.name);
    const [name, setName] = useState<string>("");

    const debouncedOnChange = useDebouncedCallback(onChange, 300);

    const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);
        debouncedOnChange({ name: event.target.value } as Guest);
    };

    useEffect(() => {
        /** When user is changing the name from Questionnaire modal directly, the `guest.name` value would be changed. So, we need to also update the local `name` input value to that. Also, on initial render update the local `name` value */
        setName(isAutoGeneratedName(guest.name) ? "" : guest.name ?? "");
    }, [guest.name, initialName]);

    const demographicName = isMembership ? "Member" : template?.name;
    const isRequired = isMembership
        ? !guest.name || isAutoGeneratedName(guest.name)
        : !guest.name && !disableRequiredFields;

    return (
        <div className="flex w-0 flex-1 items-center rounded border border-gray-light">
            <div className="min-w-[100px] max-w-[50%] overflow-hidden text-ellipsis whitespace-nowrap border-r border-gray-light px-2 py-2 leading-6">
                <DemographicIcon name={demographicName ?? "Guest"} size="medium" className="mr-1 block" />
                <NoTranslate
                    if={!isDefaultDemographic(demographicName)}
                    title={demographicName}
                    className="min-w-0 font-bold"
                >
                    {demographicName}
                </NoTranslate>
            </div>
            <div className="flex-1">
                <Input
                    data-testid="questionnaire-name-field-input"
                    isRequired={isRequired}
                    className="no-translate border-0 hover:bg-transparent"
                    placeholder={isAutoGeneratedName(guest.name) || name.length === 0 ? placeholderName : undefined}
                    value={name}
                    onChange={handleNameChange}
                />
            </div>
        </div>
    );
};
